import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { api } from '../utils/apiUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

interface PhoneNumberHistoryEntry {
  number: string;
  capturedAt: string;
}

interface UserProfile {
  fullName: string;
  email: string;
  phoneNumber: string;
  phoneNumberHistory: PhoneNumberHistoryEntry[];
  profileImageUrl: string | null;
  isAdmin?: boolean;
}

interface UserProfileContextType extends UserProfile {
  isLoadingProfile: boolean;
  isSavingProfile: boolean;
  isUploadingImage: boolean;
  animatingImageUrl: string | null; // URL of image currently undergoing animation
  uploadError: string | null; // Specific error for image upload
  profileUpdateError: string | null; // Specific error for profile text update
  successMessage: string | null;
  error: string | null; // General error state
  isAdmin: boolean; // Flag to indicate if user is admin
  login: () => Promise<void>; // login function returns a promise
  logout: () => void; // Add logout function
  fetchUserProfile: () => Promise<void>;
  setError: (message: string | null) => void; // Setter for general error
  updateProfileDetails: (details: { fullName?: string; phoneNumber?: string }, targetEmail?: string) => Promise<void>;
  startImageUploadAnimation: (objectUrl: string, forComponent?: 'settings' | 'sidebar') => void;
  processImageUpload: (file: File, animationKey: string, targetEmail?: string) => Promise<boolean>; // Returns true on success
  completeImageAnimation: (animationKey: string) => void;
  clearMessages: () => void;
  getFormattedName: (name?: string) => string;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberHistory, setPhoneNumberHistory] = useState<PhoneNumberHistoryEntry[]>([]);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [animatingImageUrl, setAnimatingImageUrl] = useState<string | null>(null); // This will store the object URL for animation
  
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [profileUpdateError, setProfileUpdateError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null); // New general error state
  const [isAdmin, setIsAdmin] = useState(false); // Flag to detect admin accessing user routes

  const logout = React.useCallback(() => {
    setFullName('');
    setEmail('');
    setPhoneNumber('');
    setPhoneNumberHistory([]);
    setProfileImageUrl(null);
    setIsAdmin(false);
    // Any other state cleanup
  }, []);

  const clearMessages = React.useCallback(() => {
    setUploadError(null);
    setProfileUpdateError(null);
    setSuccessMessage(null);
    setError(null); // Clear general error
  }, []);

  const fetchUserProfile = React.useCallback(async () => {
    console.log('[UserProfileContext] Attempting to fetch user profile...');
    setIsLoadingProfile(true);
    clearMessages(); // Clears all error/success messages, including the new general 'error'
    try {
            const response = await api.get<{ success: boolean; profile: UserProfile; message?: string }>(`/users/profile`);
      console.log('[UserProfileContext] Raw profile response received:', JSON.stringify(response.data, null, 2));

      if (response.data && response.data.success && response.data.profile) {
        const { profile } = response.data;
        console.log('[UserProfileContext] Profile data from API:', profile);
        
        // Detect if this is an admin profile accessing user routes
        const isAdminProfile = profile.isAdmin === true || 
                              profile.fullName === 'Administrator' || 
                              profile.email === 'admin' || 
                              profile.email === 'admin_user';
        setIsAdmin(isAdminProfile);
        
        if (isAdminProfile) {
          console.warn('[UserProfileContext] Admin profile detected accessing user routes');
        }
        
        setFullName(profile.fullName || '');
        setEmail(profile.email || '');
        setPhoneNumber(profile.phoneNumber || '');
        setPhoneNumberHistory(profile.phoneNumberHistory || []);
        setProfileImageUrl(profile.profileImageUrl || null);
        setError(null); // Clear any previous general error on success
      } else {
        const message = response.data?.message || 'Failed to fetch profile data. The server response was not as expected.';
        console.error('[UserProfileContext] Failed to fetch profile data. Server response:', response.data);
        setError(message); // Use the new general error state
      }
    } catch (err) {
      console.error('[UserProfileContext] Network or other error during fetch profile:', err);
      let errorMessage = 'An unexpected error occurred while fetching your profile. Please try again later.';
      if (axios.isAxiosError(err)) {
        if (err.response) {
          console.error('[UserProfileContext] Axios error response data:', err.response.data);
          console.error('[UserProfileContext] Axios error response status:', err.response.status);
          errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
        } else if (err.request) {
          console.error('[UserProfileContext] Axios error request:', err.request);
          errorMessage = 'No response from server. Please check your network connection.';
        } else {
          console.error('[UserProfileContext] Axios error message:', err.message);
          errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage); // Use the new general error state
    } finally {
      setIsLoadingProfile(false);
    }
  }, [API_URL, clearMessages, setIsLoadingProfile, setFullName, setEmail, setPhoneNumber, setPhoneNumberHistory, setProfileImageUrl, setError]);

  // The login function returns the promise from fetchUserProfile so it can be awaited
  const login = React.useCallback(() => {
    return fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    // This will now only run on initial component mount
    fetchUserProfile();
  }, [fetchUserProfile]); // fetchUserProfile is stable due to useCallback

  const updateProfileDetails = async (details: { fullName?: string; phoneNumber?: string }, targetEmail?: string) => {
    setIsSavingProfile(true);
    clearMessages();
    try {
      // When admin is in User View mode, pass targetEmail so server updates the correct user
      const payload = targetEmail ? { ...details, targetEmail } : details;
      const response = await api.put(`/users/profile`, payload);
      if (response.data.success) {
        setSuccessMessage('Profile updated successfully!');
        if (details.fullName) setFullName(details.fullName);
        if (details.phoneNumber) setPhoneNumber(details.phoneNumber);
      } else {
        setProfileUpdateError(response.data.message || 'Failed to update profile.');
      }
    } catch (err) {
      console.error('Update profile error:', err);
      setProfileUpdateError('An error occurred while updating profile.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  // `forComponent` is not strictly needed if animation is global, but kept for potential future use
  const startImageUploadAnimation = (objectUrl: string, forComponent?: 'settings' | 'sidebar') => {
    clearMessages();
    setAnimatingImageUrl(objectUrl); // This is the key for the animation
  };

  const processImageUpload = async (file: File, animationKey: string, targetEmail?: string): Promise<boolean> => {
    setIsUploadingImage(true);
    setUploadError(null); // Clear previous upload error

    const formData = new FormData();
    formData.append('profileImage', file);
    try {
      // When admin is in User View mode, pass targetEmail so server updates the correct user
      const url = targetEmail ? `/users/profile-image?targetEmail=${encodeURIComponent(targetEmail)}` : `/users/profile-image`;
      const response = await api.post<{ success: boolean; profileImageUrl?: string; message?: string }>(
        url,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      console.log('[UserProfileContext] Image upload response received:', JSON.stringify(response.data, null, 2));

      if (response.data.success && response.data.profileImageUrl) {
        console.log('[UserProfileContext] Image uploaded successfully. New URL:', response.data.profileImageUrl);
        setProfileImageUrl(response.data.profileImageUrl); // Update the actual image URL in context
        // Success message will be set by completeImageAnimation after animation
        setIsUploadingImage(false);
        return true; // Indicate success for the caller
      } else {
        const message = response.data.message || 'Failed to upload image. The server did not return a new image URL.';
        console.error('[UserProfileContext] Failed to upload profile image. Server response:', response.data);
        setUploadError(message);
        setIsUploadingImage(false);
        return false; // Indicate failure
      }
    } catch (err: any) {
      console.error('Image upload error:', err);
      // Extract actual rejection message from backend (e.g., AI validation failure)
      const backendMessage = err?.response?.data?.message;
      setUploadError(backendMessage || 'Image upload failed. Please check your connection and try again.');
      setAnimatingImageUrl(null); // Stop animation on error
      setIsUploadingImage(false);
      return false; // Indicate failure
    }
  };

  const completeImageAnimation = (animationKey: string) => {
    // This function is called by the component when its animation for 'animationKey' finishes.
    // We check if the animatingImageUrl in context still matches this key.
    // This ensures that if another upload started quickly, we don't show success for an old one.
    if (animatingImageUrl === animationKey) {
      if (!uploadError) { // If no upload error was set during processImageUpload
         setSuccessMessage('Profile image updated successfully!');
      }
      setAnimatingImageUrl(null); // Clear the animation trigger
    }
  };

  const getFormattedName = (name: string = fullName): string => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const initials = parts.slice(1).map(part => `${part.charAt(0).toUpperCase()}.`).join('');
    return `${firstName} ${initials}`.trim();
  };


  return (
    <UserProfileContext.Provider value={{
      fullName, email, phoneNumber, phoneNumberHistory, profileImageUrl, 
      isLoadingProfile, isSavingProfile, isUploadingImage, animatingImageUrl,
      uploadError, profileUpdateError,
      successMessage,
      error,
      isAdmin, // Flag indicating admin accessing user routes
      login, // Expose login function
      logout, // Expose logout function
      setError, 
      fetchUserProfile, updateProfileDetails, 
      startImageUploadAnimation, processImageUpload, completeImageAnimation, 
      clearMessages, getFormattedName
    }}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};
